
import { useEffect, useState, useRef } from 'react';
import { Coordinates, MapBounds } from '../types';
import {
  FishObservation,
  FishAbundanceCluster,
  fetchFishObservations,
  fetchGbifFishObservations,
  aggregateFishClusters,
} from '../services/fishingService';
import { boundsOverlapRatio } from '../utils/boundsUtils';
import { detectViewportCountry } from '../utils/countryDetection';

export const useFishingLayer = (
  active: boolean,
  zoom: number,
  center: Coordinates,
  currentViewBounds: MapBounds | null
) => {
  const [fishClusters, setFishClusters] = useState<FishAbundanceCluster[]>([]);
  const [fishObs, setFishObs] = useState<FishObservation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const centerRef = useRef(center);
  centerRef.current = center;
  const lastFetchedBoundsRef = useRef<MapBounds | null>(null);

  useEffect(() => {
    if (!active || zoom < 10 || !currentViewBounds) {
      setIsLoading(false);
      if (!active) {
        setFishClusters([]);
        setFishObs([]);
        setError(false);
        lastFetchedBoundsRef.current = null;
      }
      return;
    }

    if (lastFetchedBoundsRef.current && boundsOverlapRatio(lastFetchedBoundsRef.current, currentViewBounds) > 0.7) {
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        // Country-aware: Artsdatabanken for Norway, GBIF for Sweden/Denmark
        const country = detectViewportCountry(currentViewBounds);
        let observations: FishObservation[];

        if (country === 'SE' || country === 'DK') {
          observations = await fetchGbifFishObservations(
            centerRef.current, currentViewBounds, country, controller.signal
          );
        } else {
          observations = await fetchFishObservations(centerRef.current, controller.signal);
        }

        const clusters = aggregateFishClusters(observations);
        setFishObs(observations);
        setFishClusters(clusters);
        setError(false);
        lastFetchedBoundsRef.current = currentViewBounds;
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          console.warn('Fishing layer fetch failed', err);
          setFishClusters([]);
          setFishObs([]);
          setError(true);
        }
      } finally {
        setIsLoading(false);
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [active, zoom, currentViewBounds]);

  return { fishClusters, fishObs, isLoading, error };
};
